import { yasBicimle } from '@raporgor/utils';
import { Card, Table, Tag, Alert, Typography, Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { cdcFreshnessApi } from '../../api/cdcFreshness';
import type { AynaDurumu, AynaTazeligi, TabloTazeligi } from '../../api/cdcFreshness';

const DURUM: Record<AynaDurumu, { color: string; label: string }> = {
  taze:     { color: 'green',   label: 'Taze' },
  gecikmis: { color: 'orange',  label: 'Gecikmiş' },
  bayat:    { color: 'red',     label: 'Bayat' },
  hic:      { color: 'default', label: 'Hiç senkron olmamış' },
};

/**
 * Yaşı Türkçe okunur hâle getirir. Kaba taneli BİLEREK: "27 gün" ile "27 gün 3 saat"
 * arasındaki fark bu ekranda hiçbir kararı değiştirmez, ama uzun dizge rozeti sarmalar.
 */
export function MirrorFreshnessCard({ firmId }: { firmId: number }) {
  const { data, isLoading, refetch, isFetching } = useQuery<AynaTazeligi>({
    queryKey: ['cdc-freshness', firmId],
    queryFn: () => cdcFreshnessApi.get(firmId),
    enabled: !!firmId,
    refetchInterval: 60_000,
  });

  const columns = [
    { title: 'Tablo', dataIndex: 'tableName', key: 'tableName' },
    {
      title: 'Durum',
      key: 'durum',
      render: (_: unknown, r: TabloTazeligi) => (
        <Tag color={DURUM[r.durum].color}>{DURUM[r.durum].label}</Tag>
      ),
    },
    {
      title: 'Yaş',
      key: 'yas',
      render: (_: unknown, r: TabloTazeligi) => yasBicimle(r.yasSaniye),
    },
    { title: 'Faz', dataIndex: 'phase', key: 'phase' },
    {
      title: 'Son senkron',
      key: 'son',
      render: (_: unknown, r: TabloTazeligi) => new Date(r.sonSenkronUtc).toLocaleString('tr-TR'),
    },
  ];

  const durum = data ? DURUM[data.durum] : null;

  return (
    <Card
      title="Ayna Tazeliği"
      style={{ marginTop: 24 }}
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isFetching}
          data-testid="ayna-tazeligi-yenile"
        >
          Yenile
        </Button>
      }
    >
      {data && durum && (
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Space size="middle" wrap>
            <Tag color={durum.color} data-testid="ayna-durum">{durum.label}</Tag>
            <Typography.Text strong data-testid="ayna-yas">{yasBicimle(data.yasSaniye)}</Typography.Text>
            <Typography.Text type="secondary">{data.schemaName}</Typography.Text>
          </Space>

          {!data.cdcEnabled && (
            <Alert
              type="info"
              showIcon
              title="Bu firmada CDC kapalı — ayna beslenmiyor."
            />
          )}

          {data.cdcEnabled && data.durum === 'hic' && (
            <Alert
              type="warning"
              showIcon
              title="Hiç senkron kaydı yok — ayna hiç kurulmamış olabilir."
            />
          )}

          {data.cdcEnabled && data.durum === 'bayat' && (
            <Alert
              type="error"
              showIcon
              title={`Ayna ${yasBicimle(data.yasSaniye)} önce durmuş. Müşteri panelde BAYAT veri görüyor.`}
              description="Kaynak taraflı kusur olabilir (agent çevrimdışı, Change Tracking kapalı). API log'unda ilgili firmayı ara."
            />
          )}

          <Table
            size="small"
            rowKey="tableName"
            loading={isLoading}
            dataSource={data.tablolar}
            columns={columns}
            pagination={false}
            scroll={{ y: 320 }}
          />
        </Space>
      )}
    </Card>
  );
}
